import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap } from '@angular/router';
import * as FileSaver from 'file-saver';
import { isArray } from 'lodash';
import { DateTime } from 'luxon';
import { from } from 'rxjs';
import { ZendeskTicket } from 'src/app/classes/models/zendesk-ticket';
import { ZendeskTicketAttachment } from 'src/app/classes/models/zendesk-ticket-attachment';
import { ZendeskTicketComment } from 'src/app/classes/models/zendesk-ticket-comment';
import { ZendeskTicketField } from 'src/app/classes/models/zendesk-ticket-field';
import { ZendeskUser } from 'src/app/classes/models/zendesk-user';
import { UserAccountType } from 'src/app/interfaces/models/user-account-type';
import { ZendeskTicketCommentContent } from 'src/app/interfaces/zendesk-ticket-comment-content';
import { ZendeskTicketContent } from 'src/app/interfaces/zendesk-ticket-content';
import { ZendeskTicketFieldContent } from 'src/app/interfaces/zendesk-ticket-field-content';
import { ZendeskUserContent } from 'src/app/interfaces/zendesk-user-content';
import { AuthService } from 'src/app/services/auth.service';
import { ToastService } from 'src/app/services/toast.service';
import { UserService } from 'src/app/services/user.service';
import { UtilityService } from 'src/app/services/utility.service';
import { ZendeskTicketService } from 'src/app/services/zendesk-ticket.service';

@Component({
  selector: 'app-zendesk-ticket',
  templateUrl: './zendesk-ticket.component.html',
  styleUrls: ['./zendesk-ticket.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ZendeskTicketComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private route: ActivatedRoute,
    private ZendeskTicketService: ZendeskTicketService,
    private UserService: UserService,
    private ToastService: ToastService,
    private UtilityService: UtilityService,
    private AuthService: AuthService
  ) {}

  @ViewChild('fileUploadInput')
  fileUploadInputElement?: ElementRef<HTMLInputElement>;
  @Input() ZendeskTicketId?: number;
  @Input() viewOnly: boolean = false;

  ZendeskTicket?: ZendeskTicket & {
    ZendeskTicketContent: ZendeskTicketContent;
  };
  ZendeskTicketFields: (ZendeskTicketField & {
    ZendeskTicketFieldContent: ZendeskTicketFieldContent;
  })[] = [];
  ZendeskTicketComments: (ZendeskTicketComment & {
    ZendeskTicketCommentContent: ZendeskTicketCommentContent;
  })[] = [];

  ZendeskUsersMappedByZendeskUserId: {
    [key: number]: ZendeskUser & {
      ZendeskUserContent: ZendeskUserContent;
      UserAccountType?: UserAccountType;
    };
  } = {};
  ZendeskTicketFieldsMappedByZendeskTicketFieldId: {
    [key: number]: ZendeskTicketField & {
      ZendeskTicketFieldContent: ZendeskTicketFieldContent;
    };
  } = {};

  ZendeskTicketCommentContentFormGroup = new FormGroup({
    body: new FormControl<string | undefined>(
      { value: undefined, disabled: false },
      { nonNullable: true, validators: [Validators.required] }
    ),
    Attachments: new FormArray<
      FormGroup<{
        filename: FormControl<string>;
        filedata: FormControl<string>;
        filetype: FormControl<string>;
      }>
    >([])
  });

  generateTicketFieldsMultidimensionalArray(
    fields: { id: number; value: string | null }[]
  ) {
    const ProcessedFields: { id: number; value: string }[] = [];
    fields.forEach((field) => {
      const { value } = field;
      if (value !== undefined && value !== null) {
        ProcessedFields.push(field as { id: number; value: string });
      }
    });
    return this.UtilityService.constructMultidimensionalArrayFromArray<{
      id: number;
      value: string;
    }>(ProcessedFields, 2);
  }

  getZendeskUserName(ZendeskUserId: number) {
    const ZendeskUser = this.ZendeskUsersMappedByZendeskUserId[ZendeskUserId];

    if (ZendeskUser) {
      const { ZendeskUserContent, UserAccountType } = ZendeskUser;

      if (UserAccountType?.name === 'Admin') {
        return 'VIVE Admin User';
      }

      if (ZendeskUserContent.name) {
        return ZendeskUserContent.name.split(' ')[0];
      }

      return 'VIVE Admin User';
    }
    return '';
  }

  getCCEmails(CollaboratorIds: number[]) {
    const CCEmails: string[] = [];
    CollaboratorIds.forEach((CollaboratorId) => {
      const ZendeskUser =
        this.ZendeskUsersMappedByZendeskUserId[CollaboratorId];

      if (ZendeskUser) {
        const { ZendeskUserContent } = ZendeskUser;
        const { email } = ZendeskUserContent;
        if (email) {
          CCEmails.push(email);
        }
      }
    });

    return CCEmails.join('\n');
  }

  handleZendeskUser(ZendeskUser: ZendeskUser) {
    const { ZendeskUserId, userContentJSON } = ZendeskUser;

    if (!ZendeskUserId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Zendesk User Id.',
        { returnError: true }
      );
    }

    if (!userContentJSON) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Zendesk User Content.',
        { returnError: true }
      );
    }

    const { email } = userContentJSON;

    this.UserService.getUserAccountTypeForUser(email!).subscribe({
      next: (UserAccountType) => {
        const ZendeskUserModified = Object.assign(ZendeskUser, {
          ZendeskUserContent: userContentJSON
        });

        if (UserAccountType) {
          Object.assign(ZendeskUserModified, { UserAccountType });
        }

        this.ZendeskUsersMappedByZendeskUserId[ZendeskUserId] =
          ZendeskUserModified;

        this._cdr.markForCheck();
      }
    });
  }

  saveComment() {
    if (!this.ZendeskTicketId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Zendesk Ticket Id.',
        { returnError: true }
      );
    }

    if (
      this.UtilityService.generateValidationErrorsObject(
        this.ZendeskTicketCommentContentFormGroup
      )
    ) {
      throw this.ToastService.addErrorToast(
        'Error',
        'You must add a comment.',
        { returnError: true }
      );
    }

    const ZendeskTicketCommentContent = this
      .ZendeskTicketCommentContentFormGroup
      .value as ZendeskTicketCommentContent & {
      Attachments: { filename: string; filetype: string; filedata: string }[];
    };

    this.ZendeskTicketService.saveZendeskTicketCommentContent(
      this.ZendeskTicketId,
      { ZendeskTicketCommentContent }
    ).subscribe({
      next: (ZendeskTicketComment) => {
        const {
          ZendeskTicketCommentContent: SavedZendeskTicketCommentContent
        } = ZendeskTicketComment;
        const { ZendeskAuthorUser } = SavedZendeskTicketCommentContent;
        this.handleZendeskUser(ZendeskAuthorUser);
        this.ZendeskTicketComments.push(ZendeskTicketComment);
        this.ZendeskTicketCommentContentFormGroup.reset();
        this.ZendeskTicketCommentContentFormGroup.controls[
          'Attachments'
        ].clear();
        this._cdr.markForCheck();
      }
    });
  }

  // This triggers the file selection window to pop up.
  uploadFile() {
    if (this.fileUploadInputElement) {
      this.fileUploadInputElement.nativeElement.click();
    }
  }

  // Handle a file selected from the file selection window
  selectedFile(File: Event) {
    if (
      this.fileUploadInputElement &&
      File.target === this.fileUploadInputElement.nativeElement
    ) {
      const files = this.fileUploadInputElement.nativeElement.files;
      if (files) {
        const AttachmentsFormArray =
          this.ZendeskTicketCommentContentFormGroup.controls['Attachments'];
        Object.values(files).forEach((File) => {
          const { name, type } = File;
          const FileBlob = new Blob([File]);

          from(FileBlob.arrayBuffer()).subscribe({
            next: (filedata) => {
              AttachmentsFormArray.push(
                new FormGroup({
                  filename: new FormControl(name, { nonNullable: true }),
                  filetype: new FormControl(type, { nonNullable: true }),
                  filedata: new FormControl(
                    this.UtilityService.convertArrayBufferToBase64EncodedString(
                      filedata
                    ),
                    { nonNullable: true }
                  )
                })
              );
              this._cdr.markForCheck();
            }
          });
        });
      }
      this.fileUploadInputElement.nativeElement.value = '';
    }
  }

  downloadAttachment(ZendeskTicketAttachment: ZendeskTicketAttachment) {
    const { attachmentUrl, fileName } = ZendeskTicketAttachment;

    if (!attachmentUrl) {
      throw this.ToastService.addErrorToast('Error', 'Attachment has no url.', {
        returnError: true
      });
    }

    if (!fileName) {
      throw this.ToastService;
    }

    from(
      fetch(attachmentUrl, { method: 'GET', mode: 'cors', redirect: 'follow' })
    ).subscribe({
      next: (response) => {
        const { body } = response;
        if (!body) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Body property is missing from response.',
            { returnError: true }
          );
        }

        const reader = body.getReader();

        from(reader.read()).subscribe({
          next: (dataResponse) => {
            const { value } = dataResponse;
            if (!value) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Data is missing from response.',
                { returnError: true }
              );
            }

            FileSaver.saveAs(new Blob([value]), fileName);
          }
        });
      }
    });
  }

  removeCommentAttachment(index: number) {
    this.ZendeskTicketCommentContentFormGroup.controls['Attachments'].removeAt(
      index
    );
  }

  formatDate(dateText: string | undefined): string {
    if (dateText) {
      const luxonDate = DateTime.fromJSDate(new Date(dateText));
      if (luxonDate.isValid) {
        return luxonDate.toFormat('MMMM d, y HH:mm:ss');
      }
    }

    return '';
  }

  ngOnInit() {
    const paramMap: ParamMap = this.route.snapshot.paramMap;
    const ZendeskTicketId = Number(paramMap.get('ZendeskTicketId'));

    if (ZendeskTicketId) {
      this.ZendeskTicketId = ZendeskTicketId;
    }

    if (!this.ZendeskTicketId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Zendesk Ticket Id.',
        { returnError: true }
      );
    }

    const LoggedInUser = this.AuthService.getUser();

    if (!LoggedInUser) {
      throw this.ToastService.addErrorToast('Error', 'User is not logged in.', {
        returnError: true
      });
    }

    const { email } = LoggedInUser;

    if (!email) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Logged In User does not have email.',
        { returnError: true }
      );
    }

    // Calling this function without any parameters will retrieve the Zendesk User associated with the
    // Requester User's email.
    this.ZendeskTicketService.getZendeskUser().subscribe({
      next: (ZendeskUser) => {
        if (ZendeskUser) {
          this.handleZendeskUser(ZendeskUser);
        }
        this._cdr.markForCheck();
      }
    });

    this.ZendeskTicketService.getZendeskTicket({
      id: this.ZendeskTicketId,
      ZendeskTicketComment: JSON.stringify({
        options: { required: false },
        ZendeskTicketAttachment: {
          options: { required: false }
        }
      })
    }).subscribe({
      next: (ZendeskTicket) => {
        if (!ZendeskTicket) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Zendesk Ticket information.',
            { returnError: true }
          );
        }

        const {
          id: ZendeskTicketId,
          ticketContentJSON,
          ZendeskTicketComments
        } = ZendeskTicket;

        if (!ZendeskTicketId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Zendesk Ticket Id.',
            { returnError: true }
          );
        }

        if (!ticketContentJSON) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Zendesk Ticket Content.',
            { returnError: true }
          );
        }

        const ZendeskUserIds = new Set<number>();

        if (isArray(ZendeskTicketComments)) {
          ZendeskTicketComments.forEach((ZendeskTicketComment) => {
            const { ticketCommentContentJSON } = ZendeskTicketComment;

            if (!ticketCommentContentJSON) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Zendesk Ticket Comment Content.',
                { returnError: true }
              );
            }

            const ZendeskTicketCommentContent = ticketCommentContentJSON;

            const { author_id } = ZendeskTicketCommentContent;

            if (!author_id) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Zendesk Ticket Comment is missing author_id.',
                { returnError: true }
              );
            }

            ZendeskUserIds.add(author_id);

            this.ZendeskTicketComments.push(
              Object.assign(ZendeskTicketComment, {
                ZendeskTicketCommentContent
              })
            );
          });
        }

        const ZendeskTicketContent: ZendeskTicketContent = ticketContentJSON;

        this.ZendeskTicket = Object.assign(ZendeskTicket, {
          ZendeskTicketContent
        });

        const { requester_id, collaborator_ids, fields } = ZendeskTicketContent;

        ZendeskUserIds.add(requester_id);

        collaborator_ids.forEach((cid) => ZendeskUserIds.add(cid));

        this.ZendeskTicketService.getZendeskUsers(
          { ZendeskTicketId },
          {
            ZendeskUserId: JSON.stringify({ $in: Array.from(ZendeskUserIds) })
          }
        ).subscribe({
          next: (ZendeskUsers: ZendeskUser[]) => {
            ZendeskUsers.forEach((ZendeskUser) => {
              this.handleZendeskUser(ZendeskUser);
            });
            this._cdr.markForCheck();
          }
        });

        const ZendeskTicketFieldIds: number[] = [];
        fields.forEach((f) => {
          const { id, value } = f;
          if (value !== null && value !== undefined) {
            ZendeskTicketFieldIds.push(id);
          }
        });

        this.ZendeskTicketService.getZendeskTicketFields(ZendeskTicketId, {
          ZendeskTicketFieldId: JSON.stringify({ $in: ZendeskTicketFieldIds })
        }).subscribe({
          next: (ZendeskTicketFields: ZendeskTicketField[]) => {
            ZendeskTicketFields.forEach((ZendeskTicketField) => {
              const { ZendeskTicketFieldId, ticketFieldContentJSON } =
                ZendeskTicketField;

              if (!ZendeskTicketFieldId) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'Missing Zendesk Ticket Field Id.',
                  { returnError: true }
                );
              }

              if (!ticketFieldContentJSON) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'Missing Zendesk Ticket Field Content.',
                  { returnError: true }
                );
              }

              const ZendeskTicketFieldModified = Object.assign(
                ZendeskTicketField,
                { ZendeskTicketFieldContent: ticketFieldContentJSON }
              );

              this.ZendeskTicketFieldsMappedByZendeskTicketFieldId[
                ZendeskTicketFieldId
              ] = ZendeskTicketFieldModified;
              this.ZendeskTicketFields.push(ZendeskTicketFieldModified);
            });
            this._cdr.markForCheck();
          }
        });
        this._cdr.markForCheck();
      }
    });
  }
}
